import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { accountVerificationFrom } from "../lib/accountVerification";

type PublicServiceReview = {
  id: string;
  rating: number;
  raterName?: string | null;
  rateeRole?: string;
  comment?: string | null;
  contextLabel?: string | null;
};

type ServiceReviewSummary = {
  average: number;
  count: number;
  breakdown: Record<string, number>;
  reviews: PublicServiceReview[];
};

type RelatedService = {
  id: string;
  title: string;
  rating?: number;
  reviews?: number;
  from?: number;
  fromPrice?: number;
};

type ProviderVerification = {
  name: string | null;
  identity: boolean;
  company: boolean;
  memberSince: string | null;
  imageUrl: string | null;
  services: number | null;
  professions: string[];
};

export function useServiceDetails(ownerUserId: number, isBusiness: boolean, categoryQuery: string, id: string) {
  const [providerReviews, setProviderReviews] = useState<ServiceReviewSummary | null>(null);
  const [relatedServices, setRelatedServices] = useState<RelatedService[]>([]);
  const [providerVerification, setProviderVerification] = useState<ProviderVerification | null>(null);
  const [reviewState, setReviewState] = useState<"loading" | "ready" | "unavailable">("loading");
  useEffect(() => {
    if (isBusiness || !Number.isSafeInteger(ownerUserId) || ownerUserId <= 0) {
      setProviderVerification(null);
      return;
    }
    let alive = true;
    setProviderVerification(null);
    api.getProfile(ownerUserId).then(({ user: profile, counts }) => {
      if (!alive || !profile) return;
      const accountVerification = accountVerificationFrom(profile);
      setProviderVerification({
        memberSince: typeof profile.memberSince === "string" ? profile.memberSince : null,
        imageUrl: typeof profile.profileImageUrl === "string" ? profile.profileImageUrl : null,
        services: Number.isSafeInteger(counts?.services) && counts.services >= 0 ? counts.services : null,
        name: typeof profile.name === "string" ? profile.name : null,
        identity: accountVerification.identityVerified,
        company: profile.verified === true && profile.verifiedKind === "company",
        professions: accountVerification.verifiedProfessions,
      });
    }).catch(() => { if (alive) setProviderVerification(null); });
    return () => { alive = false; };
  }, [ownerUserId, isBusiness]);

  useEffect(() => {
    if (isBusiness || !Number.isSafeInteger(ownerUserId) || ownerUserId <= 0) {
      setProviderReviews(null);
      setReviewState("ready");
      return;
    }

    let alive = true;
    setReviewState("loading");
    api.userReviews(ownerUserId, "services")
      .then((response) => {
        if (alive) {
          setProviderReviews(response);
          setReviewState("ready");
        }
      })
      .catch(() => {
        if (alive) {
          setProviderReviews(null);
          setReviewState("unavailable");
        }
      });
    return () => { alive = false; };
  }, [ownerUserId, isBusiness]);

  useEffect(() => {
    let alive = true;
    api.list("services", { category: categoryQuery || undefined, limit: 6 })
      .then((items) => {
        if (!alive) return;
        setRelatedServices(items
          .filter((item: unknown): item is RelatedService => typeof item === "object" && item !== null
            && "id" in item && typeof item.id === "string" && "title" in item && typeof item.title === "string")
          .filter((item) => item.id !== id)
          .slice(0, 2));
      })
      .catch(() => { if (alive) setRelatedServices([]); });
    return () => { alive = false; };
  }, [categoryQuery, id]);

  return { providerReviews, relatedServices, providerVerification, reviewState };
}
